'use client'

import * as React from 'react'
import { useSignIn } from '@clerk/nextjs'
import { EnvelopeSimple, Key } from '@phosphor-icons/react'
import Image from 'next/image'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'

type Step = 'email' | 'code' | 'password'

export default function ForgotPasswordPage() {
  const { signIn, setActive } = useSignIn()
  const [step, setStep] = React.useState<Step>('email')
  const [isLoading, setIsLoading] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [code, setCode] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [error, setError] = React.useState('')

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!signIn) return
    setError('')
    setIsLoading(true)
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      })
      setStep('code')
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] }
      setError(clerkErr.errors?.[0]?.message || 'Could not send reset code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (verificationCode: string) => {
    if (!signIn || !verificationCode || verificationCode.length < 6) return
    setError('')
    setIsLoading(true)
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: verificationCode,
      })
      if (result.status === 'needs_new_password') {
        setStep('password')
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] }
      setError(clerkErr.errors?.[0]?.message || 'Invalid verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!signIn) return
    setError('')
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setIsLoading(true)
    try {
      const result = await signIn.resetPassword({ password })
      if (result.status === 'complete' && setActive) {
        await setActive({ session: result.createdSessionId })
        window.location.href = '/dashboard'
      } else {
        setError('Password reset could not be completed. Please try again.')
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message: string }[] }
      setError(clerkErr.errors?.[0]?.message || 'Could not reset password')
    } finally {
      setIsLoading(false)
    }
  }

  if (step === 'code') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex flex-col items-center space-y-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <EnvelopeSimple className="h-6 w-6 text-primary" />
            </div>
            <h1 className="font-serif text-2xl font-normal text-foreground">
              Check your email
            </h1>
            <p className="text-sm text-muted-foreground">
              We sent a password reset code to{' '}
              <span className="font-medium text-foreground">{email}</span>
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => {
                  setCode(value)
                  if (value.length === 6) {
                    handleVerifyCode(value)
                  }
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot className="bg-card" index={0} />
                  <InputOTPSlot className="bg-card" index={1} />
                  <InputOTPSlot className="bg-card" index={2} />
                  <InputOTPSlot className="bg-card" index={3} />
                  <InputOTPSlot className="bg-card" index={4} />
                  <InputOTPSlot className="bg-card" index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && (
              <p className="text-center text-sm text-destructive">{error}</p>
            )}
            {isLoading && (
              <p className="text-center text-sm text-muted-foreground">
                Verifying...
              </p>
            )}
          </div>

          <div className="text-center">
            <Link
              href="/sign-in"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'password') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
        <div className="w-full max-w-[400px]">
          <div className="flex flex-col items-center gap-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Key className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <h1 className="font-serif text-2xl font-normal text-foreground">
                Set new password
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Enter your new password below
              </p>
            </div>

            <form
              onSubmit={handleResetPassword}
              className="flex w-full flex-col gap-4"
            >
              <div>
                <Label htmlFor="password" className="mb-1.5">
                  New password
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-10 rounded-full bg-card px-4 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="mb-1.5">
                  Confirm new password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-10 rounded-full bg-card px-4 text-sm"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button
                type="submit"
                disabled={isLoading || !password || !confirmPassword}
                className="h-10 w-full rounded-full"
              >
                {isLoading ? 'Resetting...' : 'Reset password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-[400px]">
        <div className="flex flex-col items-center gap-6">
          <Image
            src="/favicons/haven-rounded.png"
            alt="Haven"
            width={48}
            height={48}
            className="rounded-xl"
          />

          <div className="text-center">
            <h1 className="font-serif text-2xl font-normal text-foreground">
              Forgot password
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Enter your email and we&apos;ll send you a reset code
            </p>
          </div>

          <form
            onSubmit={handleSendCode}
            className="flex w-full flex-col gap-4"
          >
            <div>
              <Label htmlFor="email" className="mb-1.5">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hello@example.com"
                className="h-10 rounded-full bg-card px-4 text-sm"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="submit"
              disabled={isLoading || !email}
              className="h-10 w-full rounded-full"
            >
              {isLoading ? 'Sending...' : 'Send reset code'}
            </Button>
          </form>

          <Link
            href="/sign-in"
            className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
